import * as Sentry from "@sentry/react";

/**
 * Centralized error logging service
 * Handles error tracking, logging, and reporting to Sentry
 */

export enum ErrorSeverity {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
  FATAL = "fatal",
}

export interface ErrorContext {
  userId?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
}

class ErrorLoggerService {
  private isInitialized = false;

  /**
   * Initialize Sentry error tracking
   * This is optional - the service will work without Sentry
   */
  initializeSentry(dsn?: string) {
    if (!dsn) {
      console.warn("Sentry DSN not provided - error tracking disabled"); // eslint-disable-line no-console
      return;
    }

    try {
      Sentry.init({
        dsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: import.meta.env.MODE,
        beforeSend(event, hint) {
          // Filter out errors that shouldn't be sent to Sentry in development
          if (!import.meta.env.PROD) {
            const error = hint.originalException;

            // Don't send certain error types in development
            if (error instanceof Error) {
              const errorCode = (error as any).code;
              // Filter network errors and Firebase permission errors in dev
              if (
                errorCode === "permission-denied" ||
                errorCode === "unavailable" ||
                error.message.includes("NetworkError") ||
                error.message.includes("Failed to fetch")
              ) {
                return null;
              }
            }
          }

          return event;
        },
      });

      this.isInitialized = true;
      console.log("Sentry initialized successfully"); // eslint-disable-line no-console
    } catch (error) {
      console.error("Failed to initialize Sentry:", error);
    }
  }

  /**
   * Log an error with context
   */
  logError(
    error: Error | string,
    severity: ErrorSeverity = ErrorSeverity.ERROR,
    context?: ErrorContext
  ) {
    const errorMessage = typeof error === "string" ? error : error.message;
    const errorObject = typeof error === "string" ? new Error(error) : error;

    // Always log to console
    if (severity === ErrorSeverity.FATAL || severity === ErrorSeverity.ERROR) {
      console.error(`[${severity.toUpperCase()}]`, errorMessage, context);
    } else if (severity === ErrorSeverity.WARNING) {
      console.warn(`[${severity.toUpperCase()}]`, errorMessage, context); // eslint-disable-line no-console
    } else {
      console.info(`[${severity.toUpperCase()}]`, errorMessage, context); // eslint-disable-line no-console
    }

    // Log to Sentry if initialized
    if (this.isInitialized) {
      Sentry.withScope((scope) => {
        scope.setLevel(severity as Sentry.SeverityLevel);

        if (context?.userId) {
          scope.setUser({ id: context.userId });
        }

        if (context?.component) {
          scope.setTag("component", context.component);
        }

        if (context?.action) {
          scope.setTag("action", context.action);
        }

        if (context?.metadata) {
          scope.setContext("metadata", context.metadata);
        }

        Sentry.captureException(errorObject, {
          level: severity as Sentry.SeverityLevel,
        });
      });
    }
  }

  /**
   * Set user context for error tracking
   */
  setUser(userId: string, email?: string, displayName?: string) {
    if (this.isInitialized) {
      Sentry.setUser({
        id: userId,
        email,
        username: displayName,
      });
    }
  }

  /**
   * Clear user context (e.g., on logout)
   */
  clearUser() {
    if (this.isInitialized) {
      Sentry.setUser(null);
    }
  }

  /**
   * Start a performance span
   * Note: This is a simplified wrapper. For complex performance tracking,
   * use Sentry.startSpan directly with a callback
   */
  startSpan(name: string, operation: string) {
    if (this.isInitialized) {
      return Sentry.startSpan({ name, op: operation }, (span) => span);
    }
    return null;
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
    if (this.isInitialized) {
      Sentry.addBreadcrumb({
        message,
        category,
        data,
        level: "info",
      });
    }
  }
}

// Export singleton instance
export const errorLogger = new ErrorLoggerService();
